// AP-106 · Portal do Cliente — Seção "Documentação" no detalhe do processo
// AP-167 · Totalmente localizado via usePortalI18n (ht / pt / en / fr / es).
// Reutiliza portalApi. Upload celular-friendly (PDF/JPG/PNG · 10MB).
import React, { useEffect, useRef, useState } from "react";
import { portalApi } from "../../context/PortalAuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  FileText, Upload, Camera, Loader2, RefreshCw,
} from "lucide-react";
import { usePortalI18n } from "./PortalI18nContext";

const STATUS_LABEL_KEY = {
  not_sent:  "docs.statusNotSent",
  received:  "docs.statusReceived",
  in_review: "docs.statusInReview",
  approved:  "docs.statusApproved",
  rejected:  "docs.statusRejected",
  expired:   "docs.statusExpired",
  correction_requested: "docs.statusCorrection",
  resubmitted: "docs.statusResubmitted",
};
const STATUS_DOT = {
  correction_requested: "bg-orange-500", resubmitted: "bg-violet-500",
  not_sent:  "bg-slate-400",  received:  "bg-amber-500",
  in_review: "bg-sky-500",    approved:  "bg-emerald-500",
  rejected:  "bg-rose-500",   expired:   "bg-slate-900",
};
const STATUS_BG = {
  correction_requested: "bg-orange-50 border-orange-400 ring-2 ring-orange-200",
  resubmitted: "bg-violet-50 border-violet-300",
  not_sent:  "bg-slate-50 border-slate-200",
  received:  "bg-amber-50 border-amber-300",
  in_review: "bg-sky-50 border-sky-300",
  approved:  "bg-emerald-50 border-emerald-300",
  rejected:  "bg-rose-50 border-rose-300",
  expired:   "bg-slate-100 border-slate-400",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
};

function DueBadge({ dueDate }) {
  const { t } = usePortalI18n();
  if (!dueDate) return null;
  const now = new Date();
  const d = new Date(dueDate + "T23:59:59");
  const diff = d.getTime() - now.getTime();
  const days = Math.floor(diff / (86400 * 1000));
  if (diff < 0) {
    return <Badge className="bg-rose-600 text-white" data-testid="portal-req-due-expired">
      {t("docs.dueExpired", { date: fmtDate(dueDate) })}
    </Badge>;
  }
  const soon = days <= 7;
  const base = t("docs.dueOn", { date: fmtDate(dueDate) });
  const trail = soon ? ` · ${t("docs.dueDaysLeft", { n: days + 1 })}` : "";
  return (
    <Badge className={soon ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-700"}
           data-testid="portal-req-due">
      {base}{trail}
    </Badge>
  );
}

function RequirementCard({ item, onUpload, uploading, highlight }) {
  const { t } = usePortalI18n();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const onPick = (accept, ref, capture) => {
    const el = ref.current;
    el.value = "";
    if (capture) el.setAttribute("capture", capture); else el.removeAttribute("capture");
    el.setAttribute("accept", accept);
    el.click();
  };
  const canSend = ["not_sent", "rejected", "correction_requested"].includes(item.status);
  const isCorrection = item.status === "correction_requested";
  return (
    <div id={`req-${item.id}`} className={`rounded-2xl border-2 p-4 space-y-2.5 transition-shadow scroll-mt-24 ${STATUS_BG[item.status]} ${highlight ? "shadow-lg" : ""}`}
         data-testid={`portal-req-${item.code}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          <div className="mt-1">
            <span className={`inline-block h-3 w-3 rounded-full ${STATUS_DOT[item.status]}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{item.name}</p>
            {item.instructions && (
              <p className="text-[11px] text-muted-foreground mt-0.5">ℹ️ {item.instructions}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge variant="outline" className="text-[10px] font-semibold"
                 data-testid={`portal-req-status-${item.code}`}>
            {t(STATUS_LABEL_KEY[item.status] || "docs.statusNotSent")}
          </Badge>
          {item.required && <span className="text-[10px] text-muted-foreground">{t("docs.required")}</span>}
          {item.latest_version_no > 1 && <span className="text-[10px] text-muted-foreground">v{item.latest_version_no}</span>}
        </div>
      </div>

      {item.status === "rejected" && item.rejection_reason && (
        <div className="rounded-lg bg-white border border-rose-300 p-2.5" data-testid={`portal-req-reason-${item.code}`}>
          <p className="text-[10px] uppercase tracking-widest text-rose-700 font-bold">{t("docs.rejectReasonTitle")}</p>
          <p className="text-xs text-rose-800 mt-0.5">{item.rejection_reason}</p>
        </div>
      )}

      {item.correction_active && item.correction_reason && ["correction_requested", "rejected"].includes(item.status) && (
        <div className="rounded-lg bg-white border border-orange-300 p-2.5" data-testid={`portal-req-correction-${item.code}`}>
          <p className="text-[10px] uppercase tracking-widest text-orange-700 font-bold">{t("docs.correctionReasonTitle")}</p>
          <p className="text-xs text-orange-900 mt-0.5">{item.correction_reason}</p>
          {item.correction_due_date && (
            <p className="text-[11px] text-orange-800 mt-1">{t("docs.correctionDue", { date: fmtDate(item.correction_due_date + "T12:00:00") })}</p>
          )}
        </div>
      )}

      {canSend && (
        <div className="pt-2 flex flex-col sm:flex-row gap-2">
          <input ref={fileRef} type="file" className="hidden"
                 accept=".pdf,image/png,image/jpeg,image/jpg,image/webp"
                 data-testid={`portal-req-file-input-${item.code}`}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(item, f); }} />
          <input ref={cameraRef} type="file" className="hidden"
                 accept="image/*" capture="environment"
                 data-testid={`portal-req-camera-input-${item.code}`}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(item, f); }} />
          <Button size="sm" className="flex-1" disabled={uploading}
                  onClick={() => onPick(".pdf,image/*", fileRef)}
                  data-testid={`portal-req-btn-file-${item.code}`}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
            {isCorrection ? t("docs.btnResendCorrection") : item.status === "rejected" ? t("docs.btnResend") : t("docs.btnSend")}
          </Button>
          <Button size="sm" variant="outline" disabled={uploading}
                  onClick={() => onPick("image/*", cameraRef, "environment")}
                  data-testid={`portal-req-btn-camera-${item.code}`}>
            <Camera className="h-3.5 w-3.5 mr-1" /> {t("docs.btnCamera")}
          </Button>
        </div>
      )}
      {item.status === "resubmitted" && (
        <p className="text-[11px] text-violet-800 italic pt-1" data-testid={`portal-req-wait-new-${item.code}`}>
          {t("docs.waitNewAnalysis")}
        </p>
      )}
      {item.status === "received" && (
        <p className="text-[11px] text-amber-800 italic pt-1">
          {t("docs.waitAnalysis")}
        </p>
      )}
      {item.status === "approved" && (
        <p className="text-[11px] text-emerald-800 italic pt-1">
          {t("docs.approvedOn", { date: item.reviewed_at ? `${t("docs.approvedOnPrefix")} ${fmtDate(item.reviewed_at)}` : "" })}
        </p>
      )}
    </div>
  );
}

export default function PortalProcessDocuments({ processId }) {
  const { t } = usePortalI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const focusDoc = new URLSearchParams(window.location.search).get("doc");
  useEffect(() => {
    if (!focusDoc || !data) return;
    const el = document.getElementById(`req-${focusDoc}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusDoc, data]);

  const load = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await portalApi.get(`/portal/processes/${processId}/requirements`);
      setData(data);
      setError("");
    } catch (e) {
      if (!silent) setError(e?.response?.data?.detail || t("docs.loadError"));
    } finally { if (!silent) setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [processId]);

  const upload = async (item, file) => {
    const MAX = 10 * 1024 * 1024;
    if (file.size > MAX) {
      toast.error(t("docs.toastTooLarge"));
      return;
    }
    const allowed = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error(t("docs.toastBadFormat"));
      return;
    }
    setUploadingId(item.id);
    try {
      const form = new FormData();
      form.append("file", file);
      await portalApi.post(
        `/portal/processes/${processId}/requirements/${item.id}/upload`,
        form, { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(t("docs.toastUploadOk"));
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("docs.toastUploadFail"));
    } finally { setUploadingId(null); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-white p-6 text-center" data-testid="portal-req-loading">
        <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        <p className="text-xs text-muted-foreground mt-2">{t("docs.loading")}</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-rose-300 bg-rose-50 p-4" data-testid="portal-req-error">
        <p className="text-sm text-rose-800">{error}</p>
      </div>
    );
  }
  if (!data || !data.has_active_request) {
    return (
      <div className="rounded-2xl border border-border bg-white p-6 text-center" data-testid="portal-req-empty">
        <FileText className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">{t("docs.emptyNoRequest")}</p>
        <p className="text-[11px] text-muted-foreground mt-1 italic">{t("docs.emptyNoRequestHint")}</p>
      </div>
    );
  }

  const p = data.progress || {};
  const pending = p.pending_count || 0;
  const complete = p.docs_complete;

  // Monta a sentença do header com <b> via dangerouslySetInnerHTML limitado a
  // valores controlados internamente (service_name/due_date vêm do backend).
  const headerHtml = t("docs.headerSummary", { n: data.items.length, svc: data.service_name || "" })
    + (data.due_date ? ` ${t("docs.headerDue")}: <b>${data.due_date}</b>.` : "")
    + (data.request?.is_legacy ? ` <span class="italic">${t("docs.headerLegacy")}</span>` : "");

  return (
    <div className="rounded-2xl border border-border bg-white p-5 space-y-4" data-testid="portal-req-section">
      <div className="rounded-xl bg-primary/5 border border-primary/30 p-3">
        <p className="text-xs font-bold text-primary uppercase tracking-widest">{t("docs.headerTitle")}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5" dangerouslySetInnerHTML={{ __html: headerHtml }} />
        {data.request?.note && (
          <p className="text-[11px] text-slate-700 mt-1">💬 {data.request.note}</p>
        )}
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <FileText className="h-4 w-4 text-primary" /> {t("docs.sectionTitle")}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("docs.sectionHint")}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load()} data-testid="portal-req-refresh" title={t("docs.btnRefresh")}>
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="space-y-1.5" data-testid="portal-req-progress">
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold">
            {t("docs.progressLabel")}: {p.required_approved || 0}/{p.required_total || 0}
          </span>
          <span className="tabular-nums text-muted-foreground">{p.percent || 0}%</span>
        </div>
        <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
          <div className={`h-full transition-all ${complete ? "bg-emerald-500" : "bg-primary"}`}
               style={{ width: `${p.percent || 0}%` }} />
        </div>
        <div className="flex items-center gap-2 flex-wrap pt-1">
          {complete && (
            <Badge className="bg-emerald-600 text-white" data-testid="portal-req-complete-badge">
              {t("docs.badgeComplete")}
            </Badge>
          )}
          {pending > 0 && !complete && (
            <Badge className="bg-amber-500 text-white" data-testid="portal-req-pending-badge">
              {t("docs.badgePending", { n: pending })}
            </Badge>
          )}
          <DueBadge dueDate={data.due_date} />
        </div>
        {complete && (
          <p className="text-[10px] text-muted-foreground italic mt-1">
            {t("docs.completeNote")}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="portal-req-list">
        {(data.items || []).map((it) => (
          <RequirementCard key={it.id} item={it} onUpload={upload}
                           uploading={uploadingId === it.id} highlight={it.id === focusDoc} />
        ))}
      </div>
    </div>
  );
}
